import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Auth0ProviderWithNavigate from "@/components/AuthProvider";
import ProtectedRoute from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import AppLayout from "@/components/layouts/AppLayout";
import RoleRedirect from "@/components/RoleRedirect";
import { ConsentBanner } from "@/components/shared/ConsentBanner";
import { PrivacyPolicyBanner } from "@/components/shared/PrivacyPolicyBanner";
import { PrivacyConsentDialog } from "@/components/shared/PrivacyConsentDialog";
import { LgpdFooter } from "@/components/shared/LgpdFooter";

import Callback from "@/pages/Callback";
import AwaitingAccess from "@/pages/AwaitingAccess";
import NotFound from "@/pages/NotFound";
import InvitePreview from "@/pages/public/InvitePreview";
import ShopProductResgate from "@/pages/public/ShopProductResgate";
import MembershipRequestPreview from "@/pages/public/MembershipRequestPreview";
import PrivacyRequestPage from "@/pages/public/PrivacyRequestPage";

// Admin
import CompanyDetails from "@/pages/admin/CompanyDetails";
import CompanySettings from "@/pages/admin/CompanySettings";
import NewCampaign from "@/pages/admin/NewCampaign";
import ShopPage from "@/pages/admin/Shop";

// Shared role-aware pages
import CampaignsPage from "@/pages/CampaignsPage";
import CampaignDetails from "@/pages/admin/CampaignDetails";
import CampaignAccept from "@/pages/CampaignAccept";

// Seller
import Sell from "@/pages/seller/Sell";

// Shared role-aware pages
import ResgatesPage from "@/pages/ResgatesPage";

// Shared
import Conversions from "@/pages/Conversions";

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Auth0ProviderWithNavigate>
          <PrivacyConsentDialog />
          <PrivacyPolicyBanner />
          <ConsentBanner />
          <Routes>
            {/* Public */}
            <Route path="/callback" element={<Callback />} />
            <Route
              path="/privacidade/solicitacao"
              element={
                <>
                  <PrivacyRequestPage />
                  <LgpdFooter />
                </>
              }
            />
            <Route
              path="/convite/:token"
              element={
                <>
                  <InvitePreview />
                  <LgpdFooter />
                </>
              }
            />
            <Route
              path="/resgate/loja/:productId"
              element={
                <>
                  <ShopProductResgate />
                  <LgpdFooter />
                </>
              }
            />
            <Route
              path="/solicitar-membro/:token"
              element={
                <>
                  <MembershipRequestPreview />
                  <LgpdFooter />
                </>
              }
            />

            {/* Smart redirect based on role */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <RoleRedirect />
                </ProtectedRoute>
              }
            />

            {/* Awaiting access (no role) */}
            <Route
              path="/aguardando"
              element={
                <ProtectedRoute>
                  <AwaitingAccess />
                </ProtectedRoute>
              }
            />

            {/* Admin-only routes */}
            <Route
              element={
                <ProtectedRoute allowedRoles={["indiqr-admin"]}>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/empresas/:id" element={<CompanyDetails />} />
              <Route path="/empresas/:companyId/campanhas/nova" element={<NewCampaign />} />
              <Route path="/configuracoes" element={<CompanySettings />} />
              <Route path="/loja" element={<ShopPage />} />
            </Route>

            {/* Campaigns — admin and influencer share the same URL, component renders by role */}
            <Route
              element={
                <ProtectedRoute allowedRoles={["indiqr-admin", "indiqr-influenciador"]}>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/campanhas" element={<CampaignsPage />} />
              <Route path="/campanhas/nova" element={<NewCampaign />} />
              <Route path="/campanhas/:id" element={<CampaignDetails />} />
              <Route path="/campanhas/:id/aceitar" element={<CampaignAccept />} />
              <Route path="/campanhas/:id/recusar" element={<CampaignAccept />} />
            </Route>

            {/* Seller routes */}
            <Route
              element={
                <ProtectedRoute allowedRoles={["indiqr-vendedor"]}>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/vender" element={<Sell />} />
            </Route>

            {/* Shared — any authenticated role */}
            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/conversoes" element={<Conversions />} />
              <Route path="/resgates" element={<ResgatesPage />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Auth0ProviderWithNavigate>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
